/* eslint-disable eqeqeq */
import { initialProductsQuantity, showMoreProducts } from 'utils/constants';
import * as CatalogTypes from './catalog.types';
import * as catalogAPI from 'api/endpoints/catalog/catalog.api';
import { catalogError } from 'utils/errorCodes';
import { notificationErr } from 'views/components/notification';
import {
  IBodyProductSearch,
  IBodyProductSearchTemp,
  ICatalogState,
  TQuerySearchArray,
} from 'interfaces/catalog.interface';
import { IBrand, IMyBrand } from 'interfaces/myBrands.interface';
import {
  IFavorite,
  IgetCatalogResponseData,
  IProductFetchDataResponse,
} from 'api/endpoints/catalog/catalog.response.interface';
import { ITranslate } from 'interfaces/general.interface';
import { TcatalogDispatchReducer } from './catalog.reducer';
import { sizeOrder } from '../utils/sizeOrder';

export async function getCatalogAction({
  dispatch,
  bodyProductSearch,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  bodyProductSearch: IBodyProductSearch;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: true });
  try {
    const products = await getAndOrderImagesCatalog({
      dispatch,
      body: bodyProductSearch,
      signal,
      token,
    });
    if (products) {
      dispatch({
        type: CatalogTypes.GET_CATALOG,
        payload: products,
      });
      const bodyProductSearchCopy: IBodyProductSearch = JSON.parse(
        JSON.stringify(bodyProductSearch)
      );
      bodyProductSearchCopy.options.index =
        bodyProductSearchCopy.options.index + showMoreProducts;
      dispatch({
        type: CatalogTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
        payload: bodyProductSearchCopy,
      });
      dispatch({
        type: CatalogTypes.SET_BODY_PRODUCT_SEARCH,
        payload: bodyProductSearchCopy,
      });
      return true;
    }
    return false;
  } catch (err: any) {
    err.code !== 20 && notificationErr(catalogError.get_catalog_001, translate);
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: false });
  }
}

export async function getRelatedProductsAction({
  dispatch,
  clientId,
  bodyProductSearch,
  associationsId,
  family,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  clientId: string;
  bodyProductSearch: IBodyProductSearch;
  associationsId: string[];
  family: string;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  const bodyProductSearchCopy: IBodyProductSearch = JSON.parse(
    JSON.stringify(bodyProductSearch)
  );
  dispatch({ type: CatalogTypes.SET_IS_LOADING_RELATED_PRODUCTS, payload: true });
  try {
    let body: IBodyProductSearch = {
      options: {
        brand_id:
          bodyProductSearchCopy.options.brand_id.length > 0
            ? bodyProductSearchCopy.options.brand_id
            : associationsId,
        client_id: [clientId],
        csv_format: false,
        favorite: null,
        with_images: null,
        search: [],
        language_id: bodyProductSearchCopy.options.language_id,
        order: [],
        group: [],
        limit: initialProductsQuantity,
        index: 0,
      },
      filter: {
        reference: [],
        color: [],
        season: [],
        segmentation: [],
        division: [],
        family: [family],
        gender: [],
        pvi: [],
        pvpr: [],
        tag: [],
        tier: [],
      },
    };

    const products = await getAndOrderImagesCatalog({
      dispatch,
      body,
      signal: undefined,
      token,
    });

    if (products && products.data.length > 0) {
      dispatch({
        type: CatalogTypes.SET_RELATED_PRODUCTS,
        payload: {
          data: products.data,
          count: products.data.length,
          active_seasons: 0,
          published_catalogs: 0,
          total: 0,
          of: 0,
        },
      });
      return true;
    } else {
      body.options.with_images = null;
      body.filter.family = [];
      body.options.limit = 30;
      body.options.index = 0;

      const products = await getAndOrderImagesCatalog({
        dispatch,
        body,
        signal: undefined,
        token,
      });

      if (products) {
        dispatch({
          type: CatalogTypes.SET_RELATED_PRODUCTS,
          payload: {
            data: products.data,
            count: products.data.length,
            active_seasons: 0,
            published_catalogs: 0,
            total: 0,
            of: 0,
          },
        });
        return true;
      }
    }
    return false;
  } catch (err) {
    notificationErr(catalogError.get_catalog_001, translate);
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING_RELATED_PRODUCTS, payload: false });
  }
}

export async function addMoreProductsToCatalogAction({
  dispatch,
  bodyProductSearchTemp,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  bodyProductSearchTemp: IBodyProductSearchTemp;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
    JSON.stringify(bodyProductSearchTemp)
  );
  if (bodyProductSearchTempCopy.options.index === 0) {
    bodyProductSearchTempCopy.options.index = 10;
  }
  dispatch({ type: CatalogTypes.SET_IS_LOADING_MORE_PRODUCTS, payload: true });
  dispatch({
    type: CatalogTypes.INCREMENT_PAGINATION_INDEX,
    payload: bodyProductSearchTempCopy.options.index + showMoreProducts,
  });
  try {
    const products = await getAndOrderImagesCatalog({
      dispatch,
      body: bodyProductSearchTempCopy,
      signal: undefined,
      token,
    });
    if (products) {
      dispatch({
        type: CatalogTypes.ADD_MORE_TO_CATALOG,
        payload: products,
      });
      return true;
    }
    return false;
  } catch (err) {
    notificationErr(catalogError.add_more_products_to_catalog_001, translate);
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING_MORE_PRODUCTS, payload: false });
  }
}

export async function addToBodySearchAction({
  dispatch,
  query,
  bodyProductSearchTemp,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  query: string;
  bodyProductSearchTemp: IBodyProductSearchTemp;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  try {
    const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
      JSON.stringify(bodyProductSearchTemp)
    );
    bodyProductSearchTempCopy.options.index = 0;
    bodyProductSearchTempCopy.options.search.push(`%${query}%`);
    return await getCatalogAction({
      dispatch,
      bodyProductSearch: bodyProductSearchTempCopy,
      translate,
      signal,
      token,
    });
  } catch (err) {
    console.log(err);
    return false;
  }
}

export async function removeFromBodySearchAction({
  dispatch,
  query,
  bodyProductSearchTemp,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  query: string;
  bodyProductSearchTemp: IBodyProductSearchTemp;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  try {
    const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
      JSON.stringify(bodyProductSearchTemp)
    );
    bodyProductSearchTempCopy.options.index = 0;
    const filteredSearch = bodyProductSearchTempCopy.options.search.filter(
      (item) => item !== query
    );
    bodyProductSearchTempCopy.options.search = filteredSearch;
    return await getCatalogAction({
      dispatch,
      bodyProductSearch: bodyProductSearchTempCopy,
      translate,
      signal,
      token,
    });
  } catch (err) {
    console.log(err);
    return false;
  }
}

export async function removeAllFromBodySearchAction({
  dispatch,
  catalogState,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  catalogState: ICatalogState;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  try {
    const catalogStateCopy: ICatalogState = JSON.parse(JSON.stringify(catalogState));
    catalogStateCopy.bodyProductSearchTemp.options.index = 0;
    catalogStateCopy.bodyProductSearchTemp.options.search = [];
    catalogStateCopy.bodyProductSearchTemp.options.favorite = '';
    catalogStateCopy.bodyProductSearchTemp.filter = {
      reference: [],
      color: [],
      season: [],
      segmentation: [],
      division: [],
      family: [],
      gender: [],
      pvi: [],
      pvpr: [],
      tag: [],
      tier: [],
    };
    catalogStateCopy.filtersLengthsTemp = {
      brand: catalogStateCopy.filtersLengths.brand,
      color: 0,
      gender: 0,
      division: 0,
      family: 0,
      season: 0,
    };
    dispatch({ type: CatalogTypes.SET_QUERY_SEARCH_ARRAY, payload: [] });
    dispatch({
      type: CatalogTypes.SET_FILTERS_LENGTHS,
      payload: catalogStateCopy.filtersLengthsTemp,
    });
    return await getCatalogAction({
      dispatch,
      bodyProductSearch: catalogStateCopy.bodyProductSearchTemp,
      translate,
      signal,
      token,
    });
  } catch (err) {
    console.log(err);
    return false;
  }
}

export async function removeFromBodySearchFilterAction({
  dispatch,
  value,
  catalogState,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  value: TQuerySearchArray;
  catalogState: ICatalogState;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  const { values, field } = value as TQuerySearchArray;
  try {
    const catalogStateCopy: ICatalogState = JSON.parse(JSON.stringify(catalogState));
    catalogStateCopy.bodyProductSearchTemp.options.index = 0;
    values.forEach((id) => {
      catalogStateCopy.bodyProductSearchTemp.filter[
        field as keyof typeof catalogStateCopy.bodyProductSearchTemp.filter
      ] = catalogStateCopy.bodyProductSearchTemp.filter[
        field as keyof typeof catalogStateCopy.bodyProductSearchTemp.filter
      ].filter((item) => item !== id);
    });
    dispatch({
      type: CatalogTypes.SET_BODY_SEARCH_FILTER,
      payload: {
        querySearchArray: catalogStateCopy.querysearchArray,
        field: field,
        value: value,
      },
    });
    return await getCatalogAction({
      dispatch,
      bodyProductSearch: catalogStateCopy.bodyProductSearchTemp,
      translate,
      signal,
      token,
    });
  } catch (err) {
    console.log(err);
    return false;
  }
}

export async function getProductAction({
  dispatch,
  productId,
  brandId,
  languageId,
  userId,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  productId: string;
  brandId: string;
  languageId: string;
  userId: string;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CatalogTypes.SET_IS_LOADING_PRODUCT, payload: true });
  try {
    const getProductFetch = await catalogAPI.getProductAPI(
      productId,
      brandId,
      languageId,
      userId,
      token
    );
    const {
      product_id,
      product_description,
      ranking,
      brand_id,
      brand_name,
      brand_logo,
      color,
      color_id,
      color_code,
      reference_id,
      reference_code,
      material,
      tags,
      images,
      ean_size,
      favorite,
      season,
      segmentation,
      family,
      division,
      gender,
      sizes,
      prices,
      other_colors,
    } = getProductFetch.data;

    // Sort by las two digits before the image url extension
    const sortedUrls = images?.sort(
      (a: { id: string; url: string }, b: { id: string; url: string }) => {
        const matchA = a.url.match(/(\d{2})\.\w+$/);
        const matchB = b.url.match(/(\d{2})\.\w+$/);

        if (!matchA) return 1;
        if (!matchB) return -1;

        const numA = parseInt(matchA[1], 10);
        const numB = parseInt(matchB[1], 10);

        // Priorizar la imagen que termina en 1
        if (numA % 10 === 1 && numB % 10 !== 1) return -1;
        if (numB % 10 === 1 && numA % 10 !== 1) return 1;

        // Si ambos terminan en 1, ordenar de menor a mayor
        if (numA % 10 === 1 && numB % 10 === 1) return numA - numB;

        return numA - numB;
      }
    );

    if (getProductFetch.response.status === 200) {
      const productState: IProductFetchDataResponse = {
        product_id: product_id,
        product_description: product_description,
        ranking: ranking,
        brand_id: brand_id,
        brand_name: brand_name,
        brand_logo: brand_logo,
        color_id: color_id,
        color_code: color_code,
        reference_id: reference_id,
        reference_code: reference_code,
        material: material,
        tags: tags,
        images: sortedUrls ?? [],
        ean_size: ean_size.sort(
          (a, b) =>
            ((sizeOrder as any)[a.size] || Infinity) -
            ((sizeOrder as any)[b.size] || Infinity)
        ),
        favorite: favorite,
        season: season,
        segmentation: segmentation,
        family: family,
        division: division,
        gender: gender,
        color: color,
        sizes: sizes.sort(
          (a, b) =>
            ((sizeOrder as any)[a] || Infinity) - ((sizeOrder as any)[b] || Infinity)
        ),
        prices: prices.sort(
          (a, b) =>
            ((sizeOrder as any)[a.size] || Infinity) -
            ((sizeOrder as any)[b.size] || Infinity)
        ),
        other_colors: other_colors.map((item: any) => {
          return {
            id: item.id,
            color: item.color_name,
            colorRef: item.color,
            image: item.url,
          };
        }),
      };
      dispatch({
        type: CatalogTypes.GET_PRODUCT,
        payload: productState,
      });
      return true;
    }
    notificationErr(catalogError.getProduct_002, translate);
    return false;
  } catch (err) {
    notificationErr(catalogError.getProduct_001, translate);
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING_PRODUCT, payload: false });
  }
}

export async function applyFiltersAction({
  dispatch,
  bodyProductSearchTemp,
  connectedBrands,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  bodyProductSearchTemp: IBodyProductSearchTemp;
  connectedBrands: IMyBrand[];
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
    JSON.stringify(bodyProductSearchTemp)
  );
  bodyProductSearchTempCopy.options.index = 0;
  try {
    if (bodyProductSearchTempCopy.options.brand_id.length === 1) {
      const findBrand = connectedBrands.find(
        (brand) => brand.tenant.brand.id === bodyProductSearchTempCopy.options.brand_id[0]
      );
      if (findBrand) {
        dispatch({
          type: CatalogTypes.SET_BRAND_SELECTED,
          payload: findBrand.tenant.brand,
        });
      }
    } else {
      dispatch({
        type: CatalogTypes.SET_BRAND_SELECTED,
        payload: {
          id: '',
          name: '',
          logo: '',
        },
      });
    }
    return await getCatalogAction({
      dispatch,
      bodyProductSearch: bodyProductSearchTempCopy,
      translate,
      signal,
      token,
    });
  } catch (err) {
    notificationErr(catalogError.filter_catalog_001, translate);
    return false;
  }
}

export function setQuerySearchAction({
  dispatch,
  query,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  query: string;
}) {
  return dispatch({ type: CatalogTypes.SET_QUERY_SEARCH, payload: query });
}

export function addBodyFiltersAction({
  dispatch,
  field,
  value,
  bodyProductSearchTemp,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  field: string;
  value: string[];
  bodyProductSearchTemp: IBodyProductSearch;
}) {
  const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
    JSON.stringify(bodyProductSearchTemp)
  );
  value.forEach((id: string) => {
    bodyProductSearchTempCopy.filter[
      field as keyof typeof bodyProductSearchTempCopy.filter
    ].push(id);
  });
  return dispatch({
    type: CatalogTypes.ADD_BODY_FILTERS,
    payload: {
      bodyProductSearchTemp: bodyProductSearchTempCopy,
      field: field,
    },
  });
}

export function removeBodyFiltersAction({
  dispatch,
  field,
  value,
  bodyProductSearchTemp,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  field: string;
  value: string[];
  bodyProductSearchTemp: IBodyProductSearch;
}) {
  const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
    JSON.stringify(bodyProductSearchTemp)
  );
  value.forEach((id: string) => {
    bodyProductSearchTempCopy.filter[
      field as keyof typeof bodyProductSearchTempCopy.filter
    ].forEach((item, index: number) => {
      if (item === id) {
        bodyProductSearchTempCopy.filter[
          field as keyof typeof bodyProductSearchTempCopy.filter
        ].splice(index, 1);
      }
    });
  });
  return dispatch({
    type: CatalogTypes.REMOVE_BODY_FILTERS,
    payload: {
      bodyProductSearchTemp: bodyProductSearchTempCopy,
      field: field,
    },
  });
}

export function addBodyBrandOptionsAction({
  dispatch,
  value,
  bodyProductSearchTemp,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  value: string[];
  bodyProductSearchTemp: IBodyProductSearchTemp;
}) {
  const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
    JSON.stringify(bodyProductSearchTemp)
  );
  value.forEach((id: string) => {
    bodyProductSearchTempCopy.options.brand_id.push(id);
  });
  return dispatch({
    type: CatalogTypes.ADD_BODY_BRAND_OPTIONS,
    payload: {
      bodyProductSearchTemp: bodyProductSearchTempCopy,
    },
  });
}

export function removeBodyBrandOptionsAction({
  dispatch,
  value,
  bodyProductSearchTemp,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  value: string[];
  bodyProductSearchTemp: IBodyProductSearchTemp;
}) {
  const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
    JSON.stringify(bodyProductSearchTemp)
  );
  value.forEach((id: string) => {
    bodyProductSearchTemp.options.brand_id.forEach((item, index: number) => {
      if (item === id) {
        bodyProductSearchTempCopy.options.brand_id.splice(index, 1);
      }
    });
  });
  return dispatch({
    type: CatalogTypes.REMOVE_BODY_BRAND_OPTIONS,
    payload: {
      bodyProductSearchTemp: bodyProductSearchTempCopy,
    },
  });
}

export function setPriceSlidersAction({
  dispatch,
  field,
  value,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  field: string;
  value: Array<number>;
}) {
  return dispatch({
    type: CatalogTypes.SET_PRICE_SLIDERS,
    payload: {
      field,
      value,
    },
  });
}

export async function resetPriceSliderAction({
  dispatch,
  type,
  bodyProductSearchTemp,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  type: string;
  bodyProductSearchTemp: IBodyProductSearchTemp;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CatalogTypes.RESET_PRICE_SLIDER, payload: { type: type } });
  const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
    JSON.stringify(bodyProductSearchTemp)
  );
  bodyProductSearchTempCopy.filter[type as keyof typeof bodyProductSearchTemp.filter] =
    [];
  bodyProductSearchTempCopy.options.index = 0;
  return await getCatalogAction({
    dispatch,
    bodyProductSearch: bodyProductSearchTempCopy,
    translate,
    signal,
    token,
  });
}

export async function setSortByAction({
  dispatch,
  sortBy,
  bodyProductSearchTemp,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  sortBy: string;
  bodyProductSearchTemp: IBodyProductSearchTemp;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  try {
    const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
      JSON.stringify(bodyProductSearchTemp)
    );
    bodyProductSearchTempCopy.options.order = [sortBy];
    bodyProductSearchTempCopy.options.index = 0;
    dispatch({ type: CatalogTypes.SET_SORT_BY, payload: sortBy });
    return await getCatalogAction({
      dispatch,
      bodyProductSearch: bodyProductSearchTempCopy,
      translate,
      signal,
      token,
    });
  } catch (error) {
    notificationErr(catalogError.sort_by_001, translate);
    return false;
  }
}

export async function getProductsByBrandsAction({
  dispatch,
  connectedBrands,
  brands,
  translate,
  bodyProductSearchTemp,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  connectedBrands: IBrand[];
  brands: IBrand[];
  translate: ITranslate;
  bodyProductSearchTemp: IBodyProductSearchTemp;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  if (brands.length === 1) {
    dispatch({ type: CatalogTypes.SET_BRAND_SELECTED, payload: brands[0] });
  } else {
    dispatch({
      type: CatalogTypes.SET_BRAND_SELECTED,
      payload: {
        id: '',
        name: '',
        logo: '',
      },
    });
  }
  try {
    const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
      JSON.stringify(bodyProductSearchTemp)
    );
    const tenants: IBrand[] = brands.length === 0 ? connectedBrands : brands;
    const tenantsId = tenants.map((item) => item.id);
    bodyProductSearchTempCopy.options.brand_id = tenantsId;
    bodyProductSearchTempCopy.options.index = 0;
    return await getCatalogAction({
      dispatch,
      bodyProductSearch: bodyProductSearchTempCopy,
      translate,
      signal,
      token,
    });
  } catch (error) {
    notificationErr(catalogError.sort_by_brand_001, translate);
    return false;
  }
}

export async function resetBodyFiltersAction({
  dispatch,
  bodyProductSearchTemp,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  bodyProductSearchTemp: IBodyProductSearchTemp;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  const bodyProductSearchTempCopy: IBodyProductSearchTemp = JSON.parse(
    JSON.stringify(bodyProductSearchTemp)
  );
  bodyProductSearchTempCopy.filter.season = [];
  bodyProductSearchTempCopy.filter.color = [];
  bodyProductSearchTempCopy.filter.segmentation = [];
  bodyProductSearchTempCopy.filter.division = [];
  bodyProductSearchTempCopy.filter.family = [];
  bodyProductSearchTempCopy.filter.gender = [];
  bodyProductSearchTempCopy.filter.pvi = [];
  bodyProductSearchTempCopy.filter.pvpr = [];
  bodyProductSearchTempCopy.filter.tag = [];
  bodyProductSearchTempCopy.filter.tier = [];
  bodyProductSearchTempCopy.options.favorite = '';
  const getCatalog = await getCatalogAction({
    dispatch,
    bodyProductSearch: bodyProductSearchTempCopy,
    translate,
    signal,
    token,
  });
  if (getCatalog) {
    dispatch({ type: CatalogTypes.RESET_BODY_FILTERS });
    return true;
  }
  return false;
}

export function setFavoritesAction({
  dispatch,
  userId,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  userId: string | null;
}) {
  dispatch({ type: CatalogTypes.INCREMENT_PAGINATION_INDEX, payload: 0 });
  return dispatch({ type: CatalogTypes.SET_FAVORITES, payload: userId });
}

export async function addLikeAction({
  dispatch,
  userId,
  productId,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  userId: string;
  productId: string;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({
    type: CatalogTypes.SET_IS_LOADING_ADD_LIKE,
    payload: {
      productId,
      isLoading: true,
    },
  });
  try {
    const likeFetch = await catalogAPI.addLikeAPI(userId, productId, token);
    if (likeFetch.response.status === 200) {
      const objectToPush: IFavorite = {
        favorite_id: likeFetch.data.id,
        user_id: userId,
      };
      dispatch({
        type: CatalogTypes.ADD_LIKE,
        payload: {
          productId,
          favorite: objectToPush,
        },
      });
      return true;
    }
    notificationErr(catalogError.addLike_002, translate);
    return false;
  } catch (err) {
    notificationErr(catalogError.addLike_001, translate);
    return false;
  } finally {
    dispatch({
      type: CatalogTypes.SET_IS_LOADING_ADD_LIKE,
      payload: {
        productId: '',
        isLoading: false,
      },
    });
  }
}

export async function removeLikeAction({
  dispatch,
  productId,
  favorites,
  userId,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  productId: string;
  favorites: IFavorite[];
  userId: string;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({
    type: CatalogTypes.SET_IS_LOADING_REMOVE_LIKE,
    payload: {
      productId,
      isLoading: true,
    },
  });
  const findFavorite = favorites.find((favorite) => favorite.user_id === userId);
  if (!findFavorite) {
    return false;
  }
  try {
    const likeFetch = await catalogAPI.removeLikeAPI(findFavorite.favorite_id, token);
    if (likeFetch.response.status === 200) {
      dispatch({
        type: CatalogTypes.REMOVE_LIKE,
        payload: {
          productId,
          favorite_id: findFavorite.favorite_id,
        },
      });
      return true;
    }
    notificationErr(catalogError.removeLike_002, translate);
    return false;
  } catch (err) {
    notificationErr(catalogError.removeLike_001, translate);
    return false;
  } finally {
    dispatch({
      type: CatalogTypes.SET_IS_LOADING_REMOVE_LIKE,
      payload: {
        productId: '',
        isLoading: false,
      },
    });
  }
}

export function addToQuerySearchArrayAction({
  dispatch,
  value,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  value: TQuerySearchArray;
}) {
  return dispatch({ type: CatalogTypes.ADD_TO_QUERY_SEARCH_ARRAY, payload: value });
}

export function removeFromQuerySearchArrayAction({
  dispatch,
  value,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  value: TQuerySearchArray;
}) {
  return dispatch({ type: CatalogTypes.REMOVE_FROM_QUERY_SEARCH_ARRAY, payload: value });
}

///////////////////////////////////
///////////////////////////////////

export async function getAndOrderImagesCatalog({
  dispatch,
  body,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  body: IBodyProductSearch;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<IgetCatalogResponseData | null> {
  const catalogFetch = await catalogAPI.getCatalogAPI({ body, token, signal });
  dispatch({ type: CatalogTypes.SET_IS_EMPTY_PRODUCTS, payload: false });
  if (catalogFetch.response.status === 404 || catalogFetch.data.data.length === 0) {
    dispatch({ type: CatalogTypes.SET_IS_EMPTY_PRODUCTS, payload: true });
  }
  if (
    catalogFetch.data.data?.length < showMoreProducts ||
    (catalogFetch.data.of !== undefined &&
      catalogFetch.data.data?.length + body.options.index >= catalogFetch.data.of)
  ) {
    dispatch({ type: CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: false });
  } else {
    dispatch({ type: CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: true });
  }
  const products = catalogFetch.data.data;

  if (products.length === 0) {
    return null;
  }

  type IImageWithNumber = {
    original: string;
    number: number;
  };

  await Promise.all(
    products.map(async (item) => {
      if (item?.images && item.images.length > 0) {
        let imagesWithNumbers: IImageWithNumber[] = [];
        let imagesWithoutNumbers: string[] = [];
        item.images.forEach((image) => {
          const number = parseInt(getExtension(image).split('.')[0], 10);
          if (!isNaN(number)) {
            imagesWithNumbers.push({ original: image, number });
          } else {
            imagesWithoutNumbers.push(image);
          }
        });
        imagesWithNumbers.sort((a, b) => a.number - b.number);
        const indexEndsWithOne = imagesWithNumbers.findIndex(
          (image) => image.number % 10 === 1
        );
        if (indexEndsWithOne > -1) {
          const [imageEndsWithOne] = imagesWithNumbers.splice(indexEndsWithOne, 1);
          imagesWithNumbers.unshift(imageEndsWithOne);
        }
        const uniqueNumericImages = Array.from(
          new Set(imagesWithNumbers.map((image) => image.original))
        );
        item.images = uniqueNumericImages.concat(imagesWithoutNumbers);
      }
    })
  );

  const result: IgetCatalogResponseData = {
    count: catalogFetch.data.count,
    data: products,
    active_seasons: catalogFetch.data.active_seasons,
    published_catalogs: catalogFetch.data.published_catalogs,
    total: catalogFetch.data.total,
    of: catalogFetch.data.of,
  };

  return result;
}

function getExtension(url: any) {
  return (url = url.substr(1 + url.lastIndexOf('/')).split('?')[0])
    .split('#')[0]
    .substr(url.lastIndexOf('.') - 2);
}
